/**
 * The languages Fazly's CONTENT ships in.
 *
 * Zero imports on purpose — this module is reachable from a bare `node --test`
 * and from the web app without dragging the barrel (and Zod) in behind it. See
 * the `//exports` note in package.json.
 *
 * ─── v2 REBUILD ─────────────────────────────────────────────────────────────
 * The reference implementation's version of this file also carried chat-room
 * routing and heuristic language DETECTION, because its community split rooms by
 * language. Fazly has no community and no rooms, so what is left is the list
 * itself and the resolution rules around it — which the marketing site, the
 * legal pages and llms.txt all depend on.
 *
 * The five are not a guess: they are the locales the live App Store listing is
 * published in (`en-US`, `de-DE`, `tr`, `pt-BR`, `es-ES`), and the localised
 * support URLs the listing points at — /support, /de/support, /tr/support,
 * /pt/support, /es/support — are indexed. Dropping one silently 404s traffic
 * that is still arriving.
 */

export const CONTENT_LANGS = ["en", "de", "tr", "pt", "es"] as const;

export type ContentLang = (typeof CONTENT_LANGS)[number];

export function isContentLang(value: unknown): value is ContentLang {
  return (
    typeof value === "string" &&
    (CONTENT_LANGS as readonly string[]).includes(value)
  );
}

export const DEFAULT_CONTENT_LANG: ContentLang = "en";

/** The language's name IN ITSELF — what a language picker must show. */
export const CONTENT_LANG_ENDONYM: Record<ContentLang, string> = {
  en: "English",
  de: "Deutsch",
  tr: "Türkçe",
  pt: "Português",
  es: "Español",
};

/**
 * Narrow an arbitrary locale tag to one of ours, or NULL when it is not one.
 *
 * Takes the primary subtag, so `pt-BR`, `pt_PT` and `pt` all land on `pt`.
 *
 * **Null, not a default.** Callers chain this over several sources — a URL path
 * segment, then a `?lang=` query, then a header — and a version that answered
 * "en" for an unrecognised input would make the FIRST source always win, so
 * every later one is dead code. `documentLang("/privacy", "?lang=tr")` has no
 * language in its path and must fall through to the query. Use
 * {@link resolveContentLang} where a default is wanted.
 */
export function contentLangOf(
  raw: string | null | undefined,
): ContentLang | null {
  if (!raw) return null;
  const primary = raw.trim().toLowerCase().split(/[-_]/)[0];
  return isContentLang(primary) ? primary : null;
}

/**
 * Pick the best of an ordered preference list — an `Accept-Language` header,
 * already split and sorted by q-value.
 *
 * First match wins, English if none do. Deliberately NOT "closest match": a
 * German speaker served Spanish because both are European is worse than a German
 * speaker served English, which is the language they are most likely to have as
 * a second one.
 */
export function resolveContentLang(
  preferences: readonly (string | null | undefined)[],
): ContentLang {
  for (const preference of preferences) {
    if (!preference) continue;
    const primary = preference.trim().toLowerCase().split(/[-_]/)[0];
    if (isContentLang(primary)) return primary;
  }
  return DEFAULT_CONTENT_LANG;
}

/**
 * The URL prefix for a language.
 *
 * English is served at the ROOT, not at `/en` — the live site's indexed English
 * pages are `/privacy`, `/support` and so on, and moving them under a prefix
 * would redirect every one of them. The other four sit under their own segment,
 * which is where the store listing already points.
 */
export function contentLangPath(lang: ContentLang): string {
  return lang === DEFAULT_CONTENT_LANG ? "" : `/${lang}`;
}
